import { useContext, FC } from 'react';
import { Button } from '@one-thd/sui-atomic-components';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { buildQueryString } from '../../utils/general';
import { isLockerPickupEligible } from '../../utils/fulfillment';
import { selectFulfillmentGroup, IOpenedFrom } from '../../utils/order-details';
import { ORDER_DETAILS_URL } from '../../constants/general';
import { PickupLockerQRCode } from '../fulfillment-details-actions/PickupLockerQRCode';
import { IDeliveryTrackerData } from '../../models/active-orders';
import { IOrderDetails } from '../../models/order-details';

interface IFulfillmentDetailsActions {
  orderDetails?: IOrderDetails;
  customerType?: string;
  /**
   * The card the drawer opened from. A mixed order carries a pickup group and
   * a delivery group at once, and only the card says which of them applies.
   */
  distribution?: IOpenedFrom;
}

export const FulfillmentDetailsActions: FC<IFulfillmentDetailsActions> = ({
  orderDetails,
  customerType,
  distribution,
}) => {
  const { channel, isConsumerApp } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';

  if (!orderDetails) return null;

  const orderNumber = orderDetails?.orderNumber;
  const storeNumber = selectFulfillmentGroup(orderDetails, distribution)
    ?.storePickup?.store?.number;

  /**
   * `deliveryTrackerData` is dead: `orderDetails` never carries it, so
   * `trackingUrl` is always empty and the Track Shipment button never
   * renders. Kept (rather than deleted) so a future payload that does carry
   * it lights this back up without a rewrite.
   */
  const deliveryTrackerData = undefined as IDeliveryTrackerData | undefined;
  const trackingUrl = (deliveryTrackerData?.links?.internalTrackingUrl
      || deliveryTrackerData?.links?.deliveryTrackerUrl)
    ?? '';

  /**
   * `orderNumbers`, `orderOrigin`, `salesDate`, `transactionId`,
   * `registerNumber`, and `storeNumber` all exist on the `orderDetails`
   * schema root, but `useOrderDetails`'s selection set never requests them,
   * so they were always `undefined` here — `buildQueryString` drops
   * undefined values, so the rendered URL has always been just
   * `?orderNumber=<n>`. Dropping them from this call is a no-op for that
   * URL, not a regression to "restore".
   */
  const orderDetailsUrl = `${ORDER_DETAILS_URL}${buildQueryString({
    orderNumber,
  })}`;

  const lockerPickupEligible = isLockerPickupEligible(
    orderDetails,
    isMobile,
    isConsumerApp,
    distribution,
  );

  return (
    <div
      className="sui-flex sui-flex-col sui-w-full sui-gap-2"
      data-testid="fulfillment-details-actions"
    >
      {trackingUrl && (
        <Button fullWidth href={trackingUrl}>
          Track Shipment
        </Button>
      )}
      <Button
        fullWidth
        variant={trackingUrl ? 'secondary' : 'primary'}
        href={orderDetailsUrl}
      >
        View Order Details
      </Button>
      {lockerPickupEligible && (
        <PickupLockerQRCode
          orderNumber={orderNumber ?? ''}
          storeNumber={storeNumber ?? ''}
          buttonVariant="primary"
          customerType={customerType || ''}
        />
      )}
    </div>
  );
};
